import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faChevronDown,
  faChevronUp,
  faGaugeHigh,
  faListCheck,
  faPenToSquare,
  faPlus,
  faTag,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import Title from "../shared/components/Title";
import apiClient from "../utils/apiClient";
import { KIND_OPTIONS, initialColumnFilters } from "../features/delivery-rules-engine/constants";
import { normalizeText, ruleConditionsText } from "../features/delivery-rules-engine/helpers";
import {
  ActiveBadge,
  ConditionsPopover,
  DeleteButton,
  FilterInput,
  KindBadge,
  StatCard,
  TagChip,
  TagFilterBar,
} from "../features/delivery-rules-engine/components/CommonComponents";
import RuleCalculator from "../features/delivery-rules-engine/components/RuleCalculator";
import RuleModal from "../features/delivery-rules-engine/components/RuleModal";

export default function DeliveryRulesEngine() {
  const [rules, setRules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [filterTags, setFilterTags] = useState([]);
  const [columnFilters, setColumnFilters] = useState(initialColumnFilters);
  const [sortConfig, setSortConfig] = useState({ key: "priority", direction: "asc" });

  const loadRules = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/api/v1/difotTools/rules");
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || err.error || "Failed to load rules");
      }
      const payload = await response.json();
      setRules(payload.rules || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  const allTags = useMemo(() => {
    const s = new Set();
    rules.forEach((r) => (r.tags || []).forEach((t) => s.add(t)));
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [rules]);

  const hasColumnFilters = useMemo(() => {
    return Object.entries(columnFilters).some(([key, value]) => {
      if (key === "status") return value !== "all";
      return String(value).trim() !== "";
    });
  }, [columnFilters]);

  const filteredRules = useMemo(() => {
    let rows = [...rules];

    if (filterTags.length > 0) {
      rows = rows.filter((r) => filterTags.some((ft) => (r.tags || []).includes(ft)));
    }

    rows = rows.filter((r) => {
      const priorityMatch = normalizeText(r.priority).includes(normalizeText(columnFilters.priority).trim());
      const nameMatch = normalizeText(r.name).includes(normalizeText(columnFilters.name).trim());
      const kindMatch = !columnFilters.kind || r.kind === columnFilters.kind;
      const daysMatch = normalizeText(r.days).includes(normalizeText(columnFilters.days).trim());
      const tagsMatch = normalizeText((r.tags || []).join(" ")).includes(normalizeText(columnFilters.tags).trim());
      const conditionsMatch = normalizeText(ruleConditionsText(r)).includes(
        normalizeText(columnFilters.conditions).trim()
      );
      const statusMatch =
        columnFilters.status === "all" ||
        (columnFilters.status === "active" && r.isActive) ||
        (columnFilters.status === "inactive" && !r.isActive);

      return priorityMatch && nameMatch && kindMatch && daysMatch && tagsMatch && conditionsMatch && statusMatch;
    });

    const getSortValue = (rule) => {
      switch (sortConfig.key) {
        case "priority":
          return Number(rule.priority ?? 0);
        case "name":
          return normalizeText(rule.name);
        case "kind":
          return normalizeText(rule.kind);
        case "days":
          return Number(rule.days ?? 0);
        case "tags":
          return normalizeText((rule.tags || []).join(" "));
        case "conditions":
          return normalizeText(ruleConditionsText(rule));
        case "status":
          return rule.isActive ? 1 : 0;
        default:
          return normalizeText(rule[sortConfig.key]);
      }
    };

    rows.sort((a, b) => {
      const av = getSortValue(a);
      const bv = getSortValue(b);
      let cmp = 0;
      if (typeof av === "number" && typeof bv === "number") {
        cmp = av - bv;
      } else {
        cmp = String(av).localeCompare(String(bv));
      }
      return sortConfig.direction === "asc" ? cmp : -cmp;
    });

    return rows;
  }, [rules, filterTags, columnFilters, sortConfig]);

  const handleColumnFilterChange = (key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const SortIcon = ({ columnKey }) => {
    const active = sortConfig.key === columnKey;
    if (!active) {
      return (
        <span className="inline-flex flex-col items-center justify-center w-3 h-3 gap-[1px] opacity-25">
          <FontAwesomeIcon icon={faChevronUp} className="w-2 h-2 block" />
          <FontAwesomeIcon icon={faChevronDown} className="w-2 h-2 block" />
        </span>
      );
    }
    return (
      <FontAwesomeIcon
        icon={sortConfig.direction === "asc" ? faChevronUp : faChevronDown}
        className="w-2.5 h-2.5 text-[#144677]"
      />
    );
  };

  const activeCount = useMemo(() => rules.filter((r) => r.isActive).length, [rules]);
  const defaultCount = useMemo(
    () => rules.filter((r) => !Object.keys(r.conditions || {}).length).length,
    [rules]
  );

  const handleDeleteRule = async (ruleId) => {
    try {
      const response = await apiClient.delete(`/api/v1/difotTools/rules/${ruleId}`);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || err.error || "Failed to delete rule");
      }
      toast.success("Rule deleted");
      await loadRules();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleFilterTag = (tag) => {
    setFilterTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] px-4 sm:px-6 py-6 space-y-5">
      <Title title="Delivery Rules Engine" />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={faListCheck} label="Total Rules" value={rules.length} color="bg-[#144677]" />
        <StatCard icon={faBolt} label="Active Rules" value={activeCount} color="bg-emerald-500" />
        <StatCard icon={faGaugeHigh} label="Default Rules" value={defaultCount} color="bg-amber-500" />
        <StatCard icon={faTag} label="Tags" value={allTags.length} color="bg-violet-500" />
      </div>

      <div className="bg-white/80 backdrop-blur-xl rounded-xl shadow-md ring-1 ring-gray-200/50 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-gray-900">Rules</h2>
            {(filterTags.length > 0 || hasColumnFilters) && (
              <span className="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                {filteredRules.length} of {rules.length}
              </span>
            )}
            {isLoading && <span className="text-xs text-gray-400 animate-pulse">Loading...</span>}
          </div>
          <div className="flex items-center gap-2">
            {hasColumnFilters && (
              <button
                type="button"
                onClick={() => setColumnFilters(initialColumnFilters)}
                className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-red-500 px-2.5 py-1.5 rounded-lg hover:bg-red-50 transition-colors border border-gray-200 hover:border-red-200"
              >
                <FontAwesomeIcon icon={faXmark} className="w-3 h-3" />
                Clear filters
              </button>
            )}
            <button
              type="button"
              onClick={() => setModal("add")}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-[#144677] text-white text-xs font-semibold hover:bg-[#11385f] transition-colors"
            >
              <FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
              Add Rule
            </button>
          </div>
        </div>

        {allTags.length > 0 && (
          <div className="px-5 pt-3 pb-0">
            <TagFilterBar
              allTags={allTags}
              selectedTags={filterTags}
              onToggle={toggleFilterTag}
              onClear={() => setFilterTags([])}
            />
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {[
                  { key: "priority", label: "Priority", className: "w-20" },
                  { key: "name", label: "Name", className: "" },
                  { key: "kind", label: "Kind", className: "w-24" },
                  { key: "days", label: "Days", className: "w-16" },
                  { key: "tags", label: "Tags", className: "" },
                  { key: "conditions", label: "Conditions", className: "" },
                  { key: "status", label: "Status", className: "w-28" },
                ].map(({ key, label, className }) => (
                  <th key={key} className={`text-left px-4 py-2.5 ${className}`}>
                    <button
                      type="button"
                      onClick={() => handleSort(key)}
                      className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
                        sortConfig.key === key ? "text-[#144677]" : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {label}
                      <SortIcon columnKey={key} />
                    </button>
                  </th>
                ))}
                <th className="w-24" />
              </tr>

              <tr className="border-b border-gray-200 bg-gray-50/60">
                <td className="px-3 py-2">
                  <FilterInput
                    value={columnFilters.priority}
                    onChange={(v) => handleColumnFilterChange("priority", v)}
                    placeholder="#"
                  />
                </td>
                <td className="px-3 py-2">
                  <FilterInput
                    value={columnFilters.name}
                    onChange={(v) => handleColumnFilterChange("name", v)}
                    placeholder="Search name..."
                  />
                </td>
                <td className="px-3 py-2">
                  <select
                    value={columnFilters.kind}
                    onChange={(e) => handleColumnFilterChange("kind", e.target.value)}
                    className={`w-full px-2 py-1.5 text-xs rounded-md border transition-colors bg-white focus:outline-none focus:ring-2 focus:ring-[#144677]/20 focus:border-[#144677] ${
                      columnFilters.kind ? "border-[#144677] text-[#144677] font-medium" : "border-gray-200 text-gray-500"
                    }`}
                  >
                    <option value="">All kinds</option>
                    {KIND_OPTIONS.map((k) => (
                      <option key={k} value={k}>{k}</option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <FilterInput
                    value={columnFilters.days}
                    onChange={(v) => handleColumnFilterChange("days", v)}
                    placeholder="Days"
                  />
                </td>
                <td className="px-3 py-2">
                  <FilterInput
                    value={columnFilters.tags}
                    onChange={(v) => handleColumnFilterChange("tags", v)}
                    placeholder="Search tags..."
                  />
                </td>
                <td className="px-3 py-2">
                  <FilterInput
                    value={columnFilters.conditions}
                    onChange={(v) => handleColumnFilterChange("conditions", v)}
                    placeholder="Search conditions..."
                  />
                </td>
                <td className="px-3 py-2">
                  <select
                    value={columnFilters.status}
                    onChange={(e) => handleColumnFilterChange("status", e.target.value)}
                    className={`w-full px-2 py-1.5 text-xs rounded-md border transition-colors bg-white focus:outline-none focus:ring-2 focus:ring-[#144677]/20 focus:border-[#144677] ${
                      columnFilters.status !== "all"
                        ? "border-[#144677] text-[#144677] font-medium"
                        : "border-gray-200 text-gray-500"
                    }`}
                  >
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </td>
                <td className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredRules.map((rule) => (
                <tr key={rule.id} className="hover:bg-gray-50/60 transition-colors">
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center justify-center w-9 h-6 rounded bg-gray-100 text-xs font-bold text-gray-600">
                      {rule.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-gray-900">{rule.name}</span>
                    {rule.stopProcessing && (
                      <span className="ml-2 text-[10px] text-orange-600 font-medium bg-orange-50 px-1.5 py-0.5 rounded ring-1 ring-orange-200">
                        stops
                      </span>
                    )}
                    {rule.reason && (
                      <p className="text-[10px] text-gray-500 mt-0.5 truncate max-w-[220px]">
                        Reason: {rule.reason}
                      </p>
                    )}
                    {rule.notes && (
                      <p className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[180px]">{rule.notes}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <KindBadge kind={rule.kind} />
                  </td>
                  <td className="px-4 py-3 text-gray-700 font-medium">{rule.days}</td>
                  <td className="px-4 py-3">
                    {(rule.tags || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {(rule.tags || []).map((tag) => (
                          <TagChip
                            key={tag}
                            tag={tag}
                            size="xs"
                            onClick={() => toggleFilterTag(tag)}
                            active={filterTags.includes(tag)}
                          />
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-300 italic">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 max-w-[200px]">
                    <ConditionsPopover conditions={rule.conditions} conditionGroups={rule.conditionGroups} />
                  </td>
                  <td className="px-4 py-3">
                    <ActiveBadge isActive={rule.isActive} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => setModal(rule)}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-[#144677] hover:bg-[#144677]/10 transition-colors"
                        title="Edit rule"
                      >
                        <FontAwesomeIcon icon={faPenToSquare} className="w-3.5 h-3.5" />
                      </button>
                      <DeleteButton onConfirm={() => handleDeleteRule(rule.id)} />
                    </div>
                  </td>
                </tr>
              ))}

              {filteredRules.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <FontAwesomeIcon icon={faListCheck} className="w-8 h-8 text-gray-200 mb-2 block mx-auto" />
                    {filterTags.length > 0 || hasColumnFilters ? (
                      <>
                        <p className="text-sm text-gray-400">No rules match the selected filters</p>
                        <button
                          type="button"
                          onClick={() => {
                            setFilterTags([]);
                            setColumnFilters(initialColumnFilters);
                          }}
                          className="mt-2 text-xs font-medium text-[#144677] hover:underline"
                        >
                          Clear filter
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-gray-400">No rules yet</p>
                        <button
                          type="button"
                          onClick={() => setModal("add")}
                          className="mt-2 text-xs font-medium text-[#144677] hover:underline"
                        >
                          Add your first rule
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <RuleCalculator />

      {modal && (
        <RuleModal
          rule={modal === "add" ? null : modal}
          allTags={allTags}
          onClose={() => setModal(null)}
          onSaved={async () => {
            setModal(null);
            await loadRules();
          }}
        />
      )}
    </div>
  );
}
